import { useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { Modal } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { IconArrowBackUp, IconUsers } from "@tabler/icons-react";
import { EmailComposer } from "../../EmailComposer";
import { useAuth } from "../../../hooks/useAuth";
import { useReceivablesEmailComposeContextQuery } from "../../../api/queries/receivablesQueries";
import { useCustomerRepresentativeQuery } from "../../../api/queries/customerOrganizationQueries";
import { useUserInfoQuery } from "../../../api/queries/userQueries";
import { useOutgoingMailboxQuery } from "../../../api/queries/organizationQueries";
import type { Email } from "../../../utils/interfaces";
import { replyQuickPrompts } from "../../../utils/consts";
import {
  buildQuotedReplyTail,
  buildReplySubject,
  computeReplyRecipients,
  EMPTY_REPLY_COMPOSE_HTML,
  parseEmailList,
  type ReplyMode,
} from "../../../utils/replyRecipients";

interface EmailReplyModalProps {
  opened: boolean;
  onClose: () => void;
  gmailThreadId: string;
  replyMode: ReplyMode;
  message: Email;
  threadSubject: string;
  linkedInvoiceIds: string[];
}

export function EmailReplyModal({
  opened,
  onClose,
  gmailThreadId,
  replyMode,
  message,
  threadSubject,
  linkedInvoiceIds,
}: EmailReplyModalProps) {
  const { email: loginEmail } = useAuth();
  const { data: userInfo } = useUserInfoQuery();
  const { data: outgoingMailbox } = useOutgoingMailboxQuery({
    enabled: opened,
  });
  const isMobile = useMediaQuery("(max-width: 48em)");
  const discardGuardRef = useRef<(() => boolean) | null>(null);

  const requestClose = () => {
    if (discardGuardRef.current && !discardGuardRef.current()) return;
    onClose();
  };

  const { data: composeContext } = useReceivablesEmailComposeContextQuery({
    gmailThreadId,
    preferEmail: message.from_email,
    enabled: opened,
  });

  const { data: customerRep } = useCustomerRepresentativeQuery(
    composeContext?.customerRepId ?? undefined,
    { enabled: opened && composeContext?.customerRepId != null },
  );

  const {
    initialTo,
    initialCc,
    initialSubject,
    initialBodyHtml,
    quotedReplyTailHtml,
  } = useMemo(() => {
    const outgoingEmail =
      outgoingMailbox?.uses_official_email &&
      outgoingMailbox.official_outgoing_email
        ? outgoingMailbox.official_outgoing_email
        : (outgoingMailbox?.mailbox_email ?? null);
    const selfContext = {
      mailboxEmail: outgoingEmail ?? userInfo?.email ?? null,
      loginEmail: loginEmail ?? null,
    };
    const { to, cc } = computeReplyRecipients(message, replyMode, selfContext);
    return {
      initialTo: to,
      initialCc: cc,
      initialSubject: buildReplySubject(message.subject || threadSubject),
      initialBodyHtml: EMPTY_REPLY_COMPOSE_HTML,
      quotedReplyTailHtml: buildQuotedReplyTail(message),
    };
  }, [
    message,
    replyMode,
    threadSubject,
    loginEmail,
    userInfo?.email,
    outgoingMailbox,
  ]);

  const modeLabel = replyMode === "reply_all" ? "Reply all" : "Reply";
  const toDisplay = parseEmailList(message.to_email).join(", ");
  const ModeIcon = replyMode === "reply_all" ? IconUsers : IconArrowBackUp;

  return (
    <Modal
      opened={opened}
      onClose={requestClose}
      title={null}
      size={isMobile ? undefined : "960px"}
      fullScreen={isMobile}
      centered
      classNames={{
        content: "bg-amber-50 max-h-[90vh] flex flex-col",
        body: "p-0 overflow-y-auto flex-1 min-h-0",
      }}
      styles={isMobile ? undefined : { content: { width: "min(960px, 95vw)" } }}
    >
      <div className="flex flex-col gap-4 p-4">
        <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex flex-wrap items-start gap-2">
            <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-0.5 text-xs font-medium uppercase tracking-wide text-amber-900">
              <ModeIcon size={14} />
              {modeLabel}
            </span>
            <h2
              className="min-w-0 flex-1 text-base font-semibold text-slate-900"
              title={threadSubject}
            >
              {threadSubject}
            </h2>
          </div>

          <p className="mt-2 text-sm text-slate-800">
            <span className="font-medium">{message.from_email}</span>
            <span className="text-slate-500"> · {message.when}</span>
          </p>
          {toDisplay ? (
            <p className="mt-1 text-xs text-slate-500">To: {toDisplay}</p>
          ) : null}
          {composeContext?.customerName ? (
            <p className="mt-1 text-xs text-slate-500">
              Customer: {composeContext.customerName}
            </p>
          ) : null}
          {linkedInvoiceIds.length > 0 ? (
            <div className="mt-3 flex flex-wrap gap-2">
              {linkedInvoiceIds.map((id) => (
                <Link
                  key={id}
                  to={`/invoice/${id}`}
                  className="rounded-full border border-amber-200 bg-amber-50 px-2.5 py-0.5 text-xs font-medium text-amber-900 hover:bg-amber-100"
                >
                  Invoice {id}
                </Link>
              ))}
            </div>
          ) : null}
        </div>

        <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
          <EmailComposer
            key={`${gmailThreadId}-${message.gmail_message_id}-${replyMode}`}
            emailDraft={null}
            threadId={gmailThreadId}
            replyMessageId={message.gmail_message_id}
            initialTo={initialTo}
            initialCc={initialCc}
            initialSubject={initialSubject}
            initialBodyHtml={initialBodyHtml}
            quotedReplyTailHtml={quotedReplyTailHtml}
            paymentExternalIds={
              linkedInvoiceIds.length > 0 ? linkedInvoiceIds : undefined
            }
            customerRepId={composeContext?.customerRepId ?? undefined}
            openInvoiceExternalIds={
              composeContext?.openInvoiceExternalIds?.length
                ? composeContext.openInvoiceExternalIds
                : undefined
            }
            language={customerRep?.language}
            navigateOnSuccess={false}
            enableAiAssist
            quickPrompts={replyQuickPrompts}
            editorMinHeight={280}
            invalidateReceivables
            discardGuardRef={discardGuardRef}
            onSendSuccess={onClose}
          />
        </div>
      </div>
    </Modal>
  );
}
